import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  Group as GroupIcon,
  ElectricMeter as MeterIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { userService, teamService, installationService } from '../services/api';

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalWorkers: 0,
    totalTeams: 0,
    totalInstallations: 0,
    todayInstallations: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [workersRes, teamsRes, installationsRes] = await Promise.all([
        userService.getUsers({ role: 'worker' }),
        teamService.getTeams(),
        installationService.getInstallations(),
      ]);

      // Calculate today's installations
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayCount = installationsRes.data.filter((inst) => {
        const instDate = new Date(inst.installation_date);
        instDate.setHours(0, 0, 0, 0);
        return instDate.getTime() === today.getTime();
      }).length;

      setStats({
        totalWorkers: workersRes.data.length,
        totalTeams: teamsRes.data.length,
        totalInstallations: installationsRes.data.length,
        todayInstallations: todayCount,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Workers',
      value: stats.totalWorkers,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: '#1976d2',
    },
    {
      title: 'Total Teams',
      value: stats.totalTeams,
      icon: <GroupIcon sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
    },
    {
      title: 'Total Installations',
      value: stats.totalInstallations,
      icon: <MeterIcon sx={{ fontSize: 40 }} />,
      color: '#ed6c02',
    },
    {
      title: 'Today\'s Installations',
      value: stats.todayInstallations,
      icon: <CheckIcon sx={{ fontSize: 40 }} />,
      color: '#9c27b0',
    },
  ];

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant='h4' gutterBottom>
        Dashboard Overview
      </Typography>
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderLeft: `4px solid ${card.color}`,
              }}
            >
              <CardContent>
                <Box display='flex' justifyContent='space-between' alignItems='flex-start'>
                  <Box>
                    <Typography color='text.secondary' gutterBottom variant='h6'>
                      {card.title}
                    </Typography>
                    <Typography variant='h3' component='div'>
                      {card.value}
                    </Typography>
                  </Box>
                  <Box sx={{ color: card.color }}>
                    {card.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant='h5' gutterBottom>
            Quick Actions
          </Typography>
          <Typography variant='body1' color='text.secondary'>
            Use the navigation menu to:
          </Typography>
          <ul>
            <li>
              <Typography variant='body1'>
                <strong>Workers:</strong> Manage field workers and assign meter credits
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>
                <strong>Teams:</strong> Create and manage teams
              </Typography>
            </li>
            <li>
              <Typography variant='body1'>
                <strong>Installations:</strong> View all meter installations and statistics
              </Typography>
            </li>
          </ul>
        </CardContent>
      </Card>
    </Box>
  );
}

